import { useTolgee } from '@tolgee/react';
import { useRouter } from 'next/router';
import { useEffect, useRef } from 'react';

import { localeOptions } from '../providers/tolgee';
import useAccessBadge from './useAccessBadge';

export function useLocale() {
  const tolgee = useTolgee();
  const router = useRouter();
  const { accessBadge, isFetched, isSuccess, isFetching } = useAccessBadge();
  const hasHydratedRef = useRef(false);
  const preferredLocale = accessBadge?.user.preferredLocale ?? getPreferredLocale();
  // Check if we're on a localized URL (e.g., /lang/es-ES or /lang/es-ES/pricing)
  const isLocalizedPage = router.pathname.startsWith('/lang/[locale]');
  const urlLocale = isLocalizedPage ? (router.query.locale as string) : null;

  const urlLocaleCandidate = urlLocale
    ? getAvailableLocaleFromCode(urlLocale)
    : undefined;
  const preferredLocaleCandidate = preferredLocale
    ? getAvailableLocaleFromCode(preferredLocale)
    : undefined;
  const targetLocale = isLocalizedPage ? urlLocaleCandidate : preferredLocaleCandidate;
  const isAccessBadgeResolved = (isFetched && !isFetching) || (!isSuccess && !isFetching);

  useEffect(() => {
    hasHydratedRef.current = true;
  }, []);

  useEffect(() => {
    if (!hasHydratedRef.current || !isAccessBadgeResolved || !targetLocale) {
      return;
    }

    const currentLanguage = tolgee.getLanguage();
    if (targetLocale === currentLanguage) {
      return;
    }

    tolgee
      .changeLanguage(targetLocale)
      .then(() => {
        storePreferredLocale(targetLocale);
      })
      .catch(() => {
        // Ignore failed language updates; we keep the active language unchanged.
      });
  }, [isAccessBadgeResolved, targetLocale, tolgee]);
}

export function usePreferredLocale() {
  const { accessBadge, isFetching } = useAccessBadge();
  const preferredLocale = accessBadge?.user.preferredLocale ?? getPreferredLocale();
  return { preferredLocale, isFetched: !isFetching };
}

export function getPreferredLocale() {
  if (typeof window !== 'undefined') {
    return localStorage.getItem('curipod.preferredLocale');
  }
  return null;
}

function setLangCookieClientSide(locale: string) {
  const maxAge = 60 * 60 * 24 * 365; // 1 year in seconds
  document.cookie = `i18nLang=${locale}; path=/; max-age=${maxAge}; samesite=lax`;
}

export function storePreferredLocale(locale: string) {
  if (typeof window !== 'undefined') {
    localStorage.setItem('curipod.preferredLocale', locale);
    setLangCookieClientSide(locale);
  }
}

export function getBrowserLanguage() {
  // Check if we're on the server side
  if (typeof window === 'undefined') {
    return undefined;
  }
  if (navigator.languages !== undefined) return navigator.languages[0];
  return navigator.language;
}

export function getAvailableLocaleFromCode(code: string): undefined | string {
  const lowerCasedCode = code.toLowerCase();
  const matchingLocale = localeOptions.find(
    ([value]) =>
      value.toLowerCase().startsWith(lowerCasedCode) ||
      lowerCasedCode.startsWith(value.toLowerCase()),
  )?.[0];
  if (matchingLocale) {
    return matchingLocale;
  }

  if (lowerCasedCode.length > 2) {
    return localeOptions.find(([value]) =>
      value.toLowerCase().startsWith(lowerCasedCode.slice(0, 2)),
    )?.[0];
  }
  return undefined;
}
